import { useCallback, useContext, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { AuthContext } from 'src/auth/context';
import { axiosInstanceNew } from 'src/utils/axios';

// One wallet-claim request at a time across the whole app, keyed by address.
// The Transactions page and the Send flow can both ask for a session at the
// same moment (e.g. right after connecting MetaMask).
const inFlightClaims = new Map();

function lower(value) {
  return String(value || '').toLowerCase();
}

function getClaimErrorMessage(error) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    'Could not start your wallet session. Please reconnect MetaMask and try again.'
  );
}

/**
 * Makes sure the backend session matches the connected wallet.
 *
 * - phone session            → ready, nothing to do
 * - wallet session, same addr → ready, nothing to do (no network call)
 * - otherwise                → POST /wallet-auth/claim, then refresh /auth/me
 *
 * The client socket re-joins the right room automatically when the AuthContext
 * user changes (see useClientSocket), so callers never reconnect it by hand.
 */
export function useWalletSession() {
  const { user, verifyUserSession } = useContext(AuthContext);
  const [checking, setChecking] = useState(false);

  const userRef = useRef(user);
  userRef.current = user;

  const ensureWalletSession = useCallback(
    async (address, { silent = false } = {}) => {
      const currentUser = userRef.current;

      if (currentUser?.sessionAuthType === 'phone') return true;

      if (!address) {
        if (!silent) toast.error('Connect MetaMask first.');
        return false;
      }

      const isWalletSession =
        currentUser?._id &&
        (currentUser?.sessionAuthType === 'wallet' || currentUser?.isWalletSession);

      if (isWalletSession && lower(currentUser?.walletAddress) === lower(address)) {
        return true;
      }

      const key = lower(address);

      if (inFlightClaims.has(key)) {
        return inFlightClaims.get(key);
      }

      const claim = (async () => {
        setChecking(true);

        try {
          const { data } = await axiosInstanceNew.post('/wallet-auth/claim', { address });

          if (data && data.ok === false) {
            if (!silent) toast.error(data?.message || 'Could not start wallet session.');
            return false;
          }

          const nextUser = await verifyUserSession({ disconnectSocketsOnFail: false });

          if (!nextUser?._id) {
            if (!silent) {
              toast.error(
                'Wallet session was created, but the app could not refresh your session.'
              );
            }
            return false;
          }

          return true;
        } catch (error) {
          console.error('[wallet-auth/claim frontend failed]', {
            status: error?.response?.status,
            data: error?.response?.data,
            message: error?.message,
          });

          if (!silent) toast.error(getClaimErrorMessage(error));
          return false;
        } finally {
          inFlightClaims.delete(key);
          setChecking(false);
        }
      })();

      inFlightClaims.set(key, claim);

      return claim;
    },
    [verifyUserSession]
  );

  return { ensureWalletSession, checking };
}

export default useWalletSession;
